// Rooms over WebSocket, WebRTC mesh between players, WebSocket relay when a peer link is not up.
// Every message is a small JSON object with a `t` field.

export interface RosterEntry { id: string; name: string; colour: string; kind: 'human' | 'bot' | 'ai'; host: boolean; }
export interface Welcome { id: string; room: string; host: string; players: RosterEntry[]; ice: RTCConfiguration; }

type Handler = (from: string, msg: Record<string, unknown>) => void;

export class RoomClient {
  ws: WebSocket | null = null;
  id = ''; room = ''; hostId = '';
  roster: RosterEntry[] = [];
  ice: RTCConfiguration = {};
  private peers = new Map<string, Peer>();
  private handlers: Handler[] = [];
  onRoster: ((roster: RosterEntry[], hostId: string, left?: string) => void) | null = null;
  onStart: ((seed: number, at: number, level?: string, flags?: string) => void) | null = null;
  onError: ((error: string) => void) | null = null;
  onClose: (() => void) | null = null;
  /** relay-only mode skips WebRTC entirely (used by headless clients) */
  relayOnly = false;
  stats = { rtcUp: 0, relayed: 0, direct: 0 };

  constructor(readonly wsUrl: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.wsUrl);
      this.ws = ws;
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error('Could not reach the game server'));
      ws.onclose = () => { this.onClose?.(); };
      ws.onmessage = (e) => { let m; try { m = JSON.parse(String(e.data)); } catch { return; } this.handle(m); };
    });
  }

  private send(msg: Record<string, unknown>): void { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg)); }

  host(name: string, colour: string, kind = 'human'): Promise<Welcome> { return this.enter({ t: 'host', name, colour, kind }); }
  join(room: string, name: string, colour: string, kind = 'human'): Promise<Welcome> { return this.enter({ t: 'join', room, name, colour, kind }); }
  private enter(msg: Record<string, unknown>): Promise<Welcome> {
    return new Promise((resolve, reject) => {
      this.pendingWelcome = { resolve, reject };
      this.send(msg);
    });
  }
  private pendingWelcome: { resolve: (w: Welcome) => void; reject: (e: Error) => void } | null = null;

  start(seed: number, level?: string, flags?: string): void { this.send({ t: 'start', seed, level, flags }); }
  updateSelf(name: string, colour: string): void { this.send({ t: 'update', name, colour }); }

  onMessage(h: Handler): void { this.handlers.push(h); }

  /** Send to one peer (or everyone when `to` is null). Unreliable channel for state, reliable for events. */
  sendTo(to: string | null, msg: Record<string, unknown>, reliable = true): void {
    const targets = to ? [to] : this.roster.filter((r) => r.id !== this.id).map((r) => r.id);
    const relayTo: string[] = [];
    for (const id of targets) {
      const peer = this.peers.get(id);
      if (peer && peer.send(msg, reliable)) this.stats.direct++;
      else relayTo.push(id);
    }
    if (!relayTo.length) return;
    this.stats.relayed++;
    if (to) this.send({ t: 'relay', to, data: msg });
    else if (relayTo.length === targets.length) this.send({ t: 'relay', data: msg });
    else for (const id of relayTo) this.send({ t: 'relay', to: id, data: msg });
  }

  private handle(m: Record<string, unknown>): void {
    switch (m.t) {
      case 'welcome': {
        const w = m as unknown as Welcome;
        this.id = w.id; this.room = w.room; this.hostId = w.host; this.roster = w.players; this.ice = w.ice ?? {};
        this.pendingWelcome?.resolve(w); this.pendingWelcome = null;
        // the newcomer offers to everyone already there
        if (!this.relayOnly) for (const r of this.roster) if (r.id !== this.id && r.kind === 'human') this.getPeer(r.id, true);
        this.onRoster?.(this.roster, this.hostId);
        break;
      }
      case 'roster': {
        this.roster = m.players as RosterEntry[]; this.hostId = m.host as string;
        if (m.left) { this.peers.get(m.left as string)?.close(); this.peers.delete(m.left as string); }
        this.onRoster?.(this.roster, this.hostId, m.left as string | undefined);
        break;
      }
      case 'error': { const e = String(m.error); if (this.pendingWelcome) { this.pendingWelcome.reject(new Error(e)); this.pendingWelcome = null; } this.onError?.(e); break; }
      case 'start': this.onStart?.(Number(m.seed), Number(m.at), m.level ? String(m.level) : undefined, m.flags ? String(m.flags) : undefined); break;
      case 'signal': { if (this.relayOnly) break; const from = m.from as string; this.getPeer(from, false).signal(m.data as Record<string, unknown>); break; }
      case 'relay': { const from = m.from as string; const data = m.data as Record<string, unknown>; for (const h of this.handlers) h(from, data); break; }
    }
  }

  private getPeer(id: string, initiator: boolean): Peer {
    let p = this.peers.get(id);
    if (!p) {
      p = new Peer(id, initiator, this.ice, (data) => this.send({ t: 'signal', to: id, data }), (msg) => { for (const h of this.handlers) h(id, msg); }, () => { this.stats.rtcUp++; });
      this.peers.set(id, p);
    }
    return p;
  }

  peerStatus(): Record<string, string> { const o: Record<string, string> = {}; for (const [id, p] of this.peers) o[id] = p.state(); return o; }

  close(): void { for (const p of this.peers.values()) p.close(); this.peers.clear(); this.ws?.close(); }
}

class Peer {
  private pc: RTCPeerConnection;
  private reliable: RTCDataChannel | null = null;
  private fast: RTCDataChannel | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private up = false;

  constructor(readonly id: string, initiator: boolean, ice: RTCConfiguration, private signalOut: (d: Record<string, unknown>) => void, private onMsg: (m: Record<string, unknown>) => void, private onUp: () => void) {
    this.pc = new RTCPeerConnection(ice);
    this.pc.onicecandidate = (e) => { if (e.candidate) this.signalOut({ candidate: e.candidate.toJSON() }); };
    this.pc.ondatachannel = (e) => this.attach(e.channel);
    this.pc.onconnectionstatechange = () => { if (this.pc.connectionState === 'failed' || this.pc.connectionState === 'closed') this.up = false; };
    if (initiator) {
      this.attach(this.pc.createDataChannel('reliable', { ordered: true }));
      this.attach(this.pc.createDataChannel('fast', { ordered: false, maxRetransmits: 0 }));
      this.pc.createOffer().then((o) => this.pc.setLocalDescription(o)).then(() => this.signalOut({ sdp: this.pc.localDescription!.toJSON() })).catch(() => {});
    }
  }

  private attach(ch: RTCDataChannel): void {
    if (ch.label === 'reliable') this.reliable = ch; else this.fast = ch;
    ch.binaryType = 'arraybuffer';
    ch.onopen = () => { if (this.reliable?.readyState === 'open' && this.fast?.readyState === 'open' && !this.up) { this.up = true; this.onUp(); } };
    ch.onclose = () => { this.up = false; };
    ch.onmessage = (e) => { try { this.onMsg(JSON.parse(String(e.data))); } catch { /* ignore */ } };
  }

  async signal(d: Record<string, unknown>): Promise<void> {
    try {
      if (d.sdp) {
        const desc = d.sdp as RTCSessionDescriptionInit;
        await this.pc.setRemoteDescription(desc);
        for (const c of this.pendingCandidates) await this.pc.addIceCandidate(c).catch(() => {});
        this.pendingCandidates = [];
        if (desc.type === 'offer') { const a = await this.pc.createAnswer(); await this.pc.setLocalDescription(a); this.signalOut({ sdp: this.pc.localDescription!.toJSON() }); }
      } else if (d.candidate) {
        const c = d.candidate as RTCIceCandidateInit;
        if (this.pc.remoteDescription) await this.pc.addIceCandidate(c).catch(() => {}); else this.pendingCandidates.push(c);
      }
    } catch (e) { console.warn('[net] signal error', e); }
  }

  send(msg: Record<string, unknown>, reliable: boolean): boolean {
    const ch = reliable ? this.reliable : this.fast;
    if (!ch || ch.readyState !== 'open') return false;
    if (!reliable && ch.bufferedAmount > 64 * 1024) return true; // drop state frames when backed up
    try { ch.send(JSON.stringify(msg)); return true; } catch { return false; }
  }

  state(): string { return `${this.pc.connectionState}/${this.reliable?.readyState ?? '-'}/${this.fast?.readyState ?? '-'}`; }
  close(): void { this.reliable?.close(); this.fast?.close(); this.pc.close(); }
}
